import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useEffect, useRef } from 'react';
import './route-map.css';

export type MapPoint = { lat: number; lng: number; label: string; title: string; done?: boolean };
type LatLng = { lat: number; lng: number };

/** Centro por defecto: Buenos Aires. */
const DEFAULT: LatLng = { lat: -34.6037, lng: -58.3816 };

const pin = (label: string, cls: string) =>
  L.divIcon({ className: '', html: `<div class="bf-map-pin ${cls}"><span>${label}</span></div>`, iconSize: [30, 30], iconAnchor: [15, 30] });

/**
 * Mapa de OpenStreetMap (gratis). Muestra el local, las paradas numeradas y el recorrido; con `onPick`, tocar el mapa
 * marca una ubicación (para ubicar a mano un cliente o el local).
 */
export function RouteMap({
  origin,
  points = [],
  onPick,
  pick,
  height = 320,
}: {
  origin?: LatLng | null;
  points?: MapPoint[];
  onPick?: (p: LatLng) => void;
  pick?: LatLng | null;
  height?: number;
}) {
  const el = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const layer = useRef<L.LayerGroup | null>(null);
  const pickRef = useRef(onPick);
  pickRef.current = onPick;

  useEffect(() => {
    if (!el.current) return;
    const m = L.map(el.current, {
      zoomControl: true,
      attributionControl: true,
      zoomAnimation: false,
      fadeAnimation: false,
      markerZoomAnimation: false,
    }).setView([DEFAULT.lat, DEFAULT.lng], 12);
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a>',
    }).addTo(m);
    m.attributionControl.setPrefix('<a href="https://leafletjs.com" target="_blank" rel="noreferrer">Leaflet</a>');
    m.on('click', (e: L.LeafletMouseEvent) => pickRef.current?.({ lat: Number(e.latlng.lat.toFixed(6)), lng: Number(e.latlng.lng.toFixed(6)) }));
    map.current = m;
    layer.current = L.layerGroup().addTo(m);
    // El mapa a veces nace dentro de un diálogo que todavía se está abriendo.
    const t = window.setTimeout(() => m.invalidateSize({ animate: false }), 250);
    return () => {
      window.clearTimeout(t);
      m.remove();
      map.current = null;
    };
  }, []);

  useEffect(() => {
    const m = map.current;
    const g = layer.current;
    if (!m || !g) return;
    g.clearLayers();
    const all: L.LatLngExpression[] = [];
    if (origin) {
      L.marker([origin.lat, origin.lng], { icon: pin('', 'is-origin'), title: 'Local de salida' }).addTo(g);
      all.push([origin.lat, origin.lng]);
    }
    for (const p of points) {
      L.marker([p.lat, p.lng], { icon: pin(p.label, p.done ? 'is-done' : ''), title: p.title })
        .bindTooltip(p.title)
        .addTo(g);
      all.push([p.lat, p.lng]);
    }
    if (all.length > 1) L.polyline(all, { color: '#0369a1', weight: 3, opacity: 0.7, dashArray: '6 6' }).addTo(g);
    if (pick) {
      L.marker([pick.lat, pick.lng], { icon: pin('', 'is-pick') }).addTo(g);
      all.push([pick.lat, pick.lng]);
    }
    if (all.length === 1) m.setView(all[0]!, 15, { animate: false });
    else if (all.length > 1) m.fitBounds(L.latLngBounds(all), { padding: [30, 30], maxZoom: 16, animate: false });
  }, [origin, points, pick]);

  return <div ref={el} className={`bf-map${onPick ? ' is-picking' : ''}`} style={{ height }} />;
}

/** Link para navegar el recorrido con Google Maps (es solo un link: no tiene costo). Hasta 9 paradas intermedias. */
export function googleRouteUrl(origin: LatLng | null | undefined, stops: LatLng[]): string | null {
  if (!stops.length) return null;
  const s = stops.slice(0, 10);
  const f = (p: LatLng) => `${p.lat},${p.lng}`;
  const q = new URLSearchParams({ api: '1', travelmode: 'driving', destination: f(s[s.length - 1]!) });
  if (origin) q.set('origin', f(origin));
  if (s.length > 1) q.set('waypoints', s.slice(0, -1).map(f).join('|'));
  return `https://www.google.com/maps/dir/?${q.toString()}`;
}
